<template>
  <ModalContainer :is-show-modal="isShowModal" @close="closeModal">
    <template #header>
      <div>{{ $t(modalText) }}</div>
      <IconButton icon="cross" @click="closeModal" />
    </template>

    <SelectTab
      v-if="isSelectTabVisible"
      :options="cooperationOptions"
      :value="currentCooperationType"
      @select="selectCooperationType"
    >
      <template #option="slotProps">
        {{ $t(slotProps.option.value) }}
      </template>
    </SelectTab>

    <form class="modal-form" @submit.prevent="onSubmit">
      <div class="modal-form-body">
        <BaseInput
          v-model="formStateForValidation.name"
          :placeholder="$t('Name')"
          :inner-placeholder="$t('Your name')"
          :error="errors?.name?.[0]"
          mark-necessary
        />
        <BaseInput
          v-model="formStateForValidation.phone"
          label="phone"
          :placeholder="$t('Phone')"
          :inner-placeholder="phoneNumberFormat[countryCode].placeholder"
          :error="errors?.phone?.[0]"
          :validator="validators.phone.phoneValidator(countryCode)"
          :unavailable-symbols="validators.phone.unavailableSymbols"
          @is-error="validationErrors['phone']"
          type="tel"
          :help-text="$t('contacts.formatNumber')"
          :trim="true"
        />
        <BaseInput
          v-model="formStateForValidation.email"
          label="email"
          :placeholder="$t('Email')"
          :inner-placeholder="emailPlaceholder"
          :error="errors?.email?.[0]"
          :validator="validators.email"
          @is-error="validationErrors['email']"
          mark-necessary
        />
        <BaseInput
          v-model="formStateForValidation.companyName"
          :placeholder="preparedCompanyInfoTitle.placeholder"
          :inner-placeholder="preparedCompanyInfoTitle.innerPlaceholder"
          :error="errors?.companyName?.[0]"
          mark-necessary
        />
        <BaseInput
          v-model="formStateWithoutValidation.location"
          :placeholder="$t('City')"
          :inner-placeholder="$t('Enter the city')"
        />
        <AgreementCheckbox v-model="formStateForValidation.processPersonalData" />
      </div>
      <div class="modal-form-control">
        <RecaptchaV2Fallback v-if="showV2Captcha" @verify="onV2Verify" />
        <BaseButton
          type="submit"
          :loading="loading"
          :disabled="isDisabledButton || showV2Captcha"
        >
          {{ $t("Send") }}
        </BaseButton>
      </div>
    </form>

    <div class="modal-body__separator">
      <hr />
      <span class="text-18-500">{{ $t("or") }}</span>
      <hr />
    </div>

    <a
      class="modal-body__phone"
      :href="`tel:${officeContact?.phone}`"
      @click="reportAboutContact"
    >
      <BaseIcon icon="phone" />
      <span class="text-18-500">{{ officeContact?.phone }}</span>
    </a>
  </ModalContainer>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { PropType } from "vue";

import { useConfigStore } from "~/stores/config";
import useInputValidation from "~/composables/useInputValidation";
import useWindowResize from "~/composables/useWindowResize";
import { CooperationTypes, SuccessModalTypes } from "~/types/entities";
import { phoneNumberFormat } from "~/utils/validators/phone";
import ModalContainer from "~/components/modals/ModalContainer.vue";
import BaseInput from "~/components/input/BaseInput.vue";
import SelectTab from "~/components/input/SelectTab/SelectTab.vue";
import validators from "~/utils/validators";
import AgreementCheckbox from "~/components/agreementCheckbox/AgreementCheckbox.vue";
import BaseButton from "~/components/button/BaseButton.vue";
import {
  COOPERATION_OPTIONS_DEFAULT,
  COOPERATION_OPTIONS_TABLET,
} from "~/components/sections/GetDetailsSection.vue";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import IconButton from "~/components/button/IconButton.vue";

import { successModalKey } from "~/symbols";
import { useContentStore } from "~/stores/content";

import isEqual from "lodash/isEqual";
import { hidePD } from "~/utils/analytics";
import { submitRecaptcha } from "~/api/formRequests";
import { useReCaptcha } from "vue-recaptcha-v3";
import RecaptchaV2Fallback from "~/components/recaptcha/V2Fallback.vue";
import getEmailPlaceHolder from "~/utils/getEmailPlaceholder";

const MODAL_TEXT = {
  [CooperationTypes.PARTNER]: "Become a Partner",
  [CooperationTypes.CONNECT_COMPANY]: "Get an Offer",
};

const emit = defineEmits(["update:isShowModal", "closeModal"]);

const props = defineProps({
  isShowModal: {
    type: Boolean as PropType<boolean>,
    default: false,
  },

  cooperationType: {
    type: String as PropType<CooperationTypes | null>,
    default: null,
  },
});

const { gtag } = useGtag();
const contentStore = useContentStore();
const { getContacts: officeContact } = storeToRefs(contentStore);

const { brand } = useRuntimeConfig().public;

const { t } = useI18n();
const { isMobileScreen } = useWindowResize();
const { countryCode } = storeToRefs(useConfigStore());
const cooperationOptions = computed(() =>
  isMobileScreen.value
    ? COOPERATION_OPTIONS_DEFAULT
    : COOPERATION_OPTIONS_TABLET
);
const isSelectTabVisible = computed(() => !props.cooperationType);
const cooperationType = ref(
  props.cooperationType || CooperationTypes.CONNECT_COMPANY
);
const currentCooperationType = computed(
  () => props.cooperationType || cooperationType.value
);
const modalText = computed(() => MODAL_TEXT[currentCooperationType.value]);

const defaultStateForValidation = {
  companyName: "",
  name: "",
  email: "",
  phone: "",
  processPersonalData: false,
  country: countryCode.value,
};
const formStateForValidation = reactive({ ...defaultStateForValidation });

const defaultStateWithoutValidation = {
  location: "",
};

// In the future, pay attention to the `gEventPayloadWithoutPD` variable
// if sensitive data will be stored here
const formStateWithoutValidation = reactive({ ...defaultStateWithoutValidation });

const loading = ref(false);
const errors = ref<any>(null);
const showV2Captcha = ref(false);
const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

const gEventPayloadWithoutPD = computed(() => {
  const formStateWithValidationAndWithoutPD = isEqual(formStateForValidation, defaultStateForValidation) ? undefined : hidePD(formStateForValidation);
  return { ...formStateWithValidationAndWithoutPD, ...formStateWithoutValidation };
});

watch(
  () => formStateForValidation,
  () => {
    if (errors.value) {
      errors.value = null;
    }
  },
  { deep: true }
);

const { isValid, validationErrors } = useInputValidation(formStateForValidation, { optionalFields: ["phone"] });
const isDisabledButton = computed(() => !isValid.value || !!errors.value);

const preparedPhone = computed(() => removePlusSymbol(formStateForValidation.phone));
const preparedCompanyInfoTitle = computed(() => {
  const isPartner = currentCooperationType.value === CooperationTypes.PARTNER;

  const titlePlaceholder = isPartner ? t("Facility name") : t("Company name");
  const innerPlaceholder = isPartner ? t("Facility name") : t("Company");

  return {
    placeholder: titlePlaceholder,
    innerPlaceholder: innerPlaceholder
  };
});

const showSuccessModal = inject(successModalKey, () => {});

function closeModal() {
  gtag("event", GetOfferModalEvents.close, gEventPayloadWithoutPD.value);
  resetState();
  emit("update:isShowModal", false);
  emit("closeModal");
}

function resetState() {
  Object.assign(formStateForValidation, defaultStateForValidation);
  Object.assign(formStateWithoutValidation, defaultStateWithoutValidation);
  showV2Captcha.value = false;
}

async function onSubmit() {
  gtag("event", GetOfferModalEvents.submit, gEventPayloadWithoutPD.value);

  loading.value = true;
  try {
    const token = await getV3Token();
    const { success } = await submitRecaptcha({
      form: {
        ...formStateForValidation,
        ...formStateWithoutValidation,
        phone: preparedPhone.value,
      },
      token,
      formType: currentCooperationType.value,
    });

    if (!success) {
      showV2Captcha.value = true;
      return;
    }

    handleSuccessfulSubmit();
  } catch (error: any) {
    handleSubmitError(error);
  } finally {
    loading.value = false;
  }
}

async function onV2Verify(v2Token: string) {
  loading.value = true;
  try {
    const { success } = await submitRecaptcha({
      form: {
        ...formStateForValidation,
        ...formStateWithoutValidation,
        phone: preparedPhone.value,
      },
      v2Token,
      formType: currentCooperationType.value,
    });

    if (!success) {
      throw new Error("reCAPTCHA verification failed");
    }

    handleSuccessfulSubmit();
  } catch (error: any) {
    handleSubmitError(error);
  } finally {
    loading.value = false;
  }
}

function handleSuccessfulSubmit() {
  closeModal();
  if (CooperationTypes.CONNECT_COMPANY === currentCooperationType.value) {
    showSuccessModal(SuccessModalTypes.CONNECT_COMPANY);
  } else {
    showSuccessModal(SuccessModalTypes.PARTNER);
  }
}

function handleSubmitError(error: any) {
  if (error?.data?.errors) {
    errors.value = error.data.errors;
  }
  closeModal();
  showSuccessModal(error?.data?.modalType || SuccessModalTypes.ISSUE);
}

async function getV3Token() {
  await recaptchaLoaded();
  const token = await executeRecaptcha("submit");
  if (!token) throw new Error("reCAPTCHA token missing");
  return token;
}

const emailPlaceholder = getEmailPlaceHolder(brand);

function selectCooperationType(optionId: CooperationTypes) {
  gtag("event", GetOfferModalEvents[optionId], gEventPayloadWithoutPD.value);
  cooperationType.value = optionId;
}

const reportAboutContact = () =>  gtag("event", GetOfferModalEvents.support, gEventPayloadWithoutPD.value);
</script>
